/**
 * Browser-safe Link B token formatting (no node:crypto).
 * Generation / hashing live in `proposalLinkBToken.ts` (server).
 *
 * New tokens: exactly 8 Crockford Base32 characters, no separators.
 * Legacy tokens: 26 Crockford characters (optionally hyphen-grouped for display).
 */

export const PROPOSAL_LINK_B_TOKEN_BYTES = 5;
/** Newly issued Link B bearer tokens. */
export const PROPOSAL_LINK_B_TOKEN_CROCKFORD_LENGTH = 8;
/** Pre-shortening production tokens — still accepted by the resolver. */
export const PROPOSAL_LINK_B_LEGACY_TOKEN_CROCKFORD_LENGTH = 26;
export const PROPOSAL_LINK_B_SHORT_CODE_LENGTH = 6;

const CROCKFORD_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const CROCKFORD_CHAR_RE = /^[0-9A-HJKMNP-TV-Z]+$/;

const DECODE_MAP: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  for (let i = 0; i < CROCKFORD_ALPHABET.length; i++) {
    map[CROCKFORD_ALPHABET[i]!] = i;
    map[CROCKFORD_ALPHABET[i]!.toLowerCase()] = i;
  }
  map.O = 0;
  map.o = 0;
  map.I = 1;
  map.i = 1;
  map.L = 1;
  map.l = 1;
  return map;
})();

export { CROCKFORD_ALPHABET };

/** Strip display hyphens and normalize ambiguous Crockford characters. */
export function normalizeProposalLinkBToken(raw: string): string {
  return String(raw ?? "")
    .trim()
    .replace(/[-\s_]/g, "")
    .toUpperCase()
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1");
}

export function encodeCrockfordBase32(bytes: Uint8Array): string {
  if (bytes.length === 0) return "";
  let bits = 0;
  let value = 0;
  let output = "";
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8) | bytes[i]!;
    bits += 8;
    while (bits >= 5) {
      output += CROCKFORD_ALPHABET[(value >>> (bits - 5)) & 31]!;
      bits -= 5;
    }
  }
  if (bits > 0) {
    output += CROCKFORD_ALPHABET[(value << (5 - bits)) & 31]!;
  }
  return output;
}

export function decodeCrockfordBase32(encoded: string): Uint8Array | null {
  const normalized = normalizeProposalLinkBToken(encoded);
  if (!normalized) return null;
  let bits = 0;
  let value = 0;
  const out: number[] = [];
  for (const ch of normalized) {
    const idx = DECODE_MAP[ch];
    if (idx === undefined) return null;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return Uint8Array.from(out);
}

/**
 * Display form for URL embedding.
 * - 8-char tokens: no separators (`7XP4MQ2V`)
 * - Legacy 26-char tokens: `XXXX-XXXX-XXXX-XXXX-XXXX-XX`
 */
export function formatProposalLinkBTokenDisplay(token: string): string {
  const n = normalizeProposalLinkBToken(token);
  if (n.length === PROPOSAL_LINK_B_TOKEN_CROCKFORD_LENGTH) {
    return n;
  }
  if (n.length === PROPOSAL_LINK_B_LEGACY_TOKEN_CROCKFORD_LENGTH) {
    return `${n.slice(0, 4)}-${n.slice(4, 8)}-${n.slice(8, 12)}-${n.slice(12, 16)}-${n.slice(16, 20)}-${n.slice(20, 26)}`;
  }
  throw new Error("Invalid Link B token length for display formatting.");
}

export function isWellFormedProposalLinkBToken(token: string): boolean {
  const normalized = normalizeProposalLinkBToken(token);
  if (
    normalized.length !== PROPOSAL_LINK_B_TOKEN_CROCKFORD_LENGTH &&
    normalized.length !== PROPOSAL_LINK_B_LEGACY_TOKEN_CROCKFORD_LENGTH
  ) {
    return false;
  }
  return CROCKFORD_CHAR_RE.test(normalized);
}
